export const GoogleAdsConfig = {
  // Account-level, so the same id is declared in the paid-ads landing page
  // served by the fe-router worker (cloudflare-workers/fe-router/src/index.ts).
  // Keep the two in sync. The label is per conversion action and lives here
  // only, since that page never fires a conversion.
  conversionId: "AW-428060457",
  prodInstallLabel: "ZXp0CJ2sqvMcEKnejswB",
};

export type GoogleAdsConfigType = typeof GoogleAdsConfig;

const PROD_UI_URL = "https://app.intelligems.io";

/**
 * Reporting is production-only. Every environment shares one Google Ads
 * account, so a conversion fired from a dev stage or from beta lands in the
 * live account and can reach automated bidding.
 *
 * Keyed on `VITE_UI_URL`, **not** `VITE_STAGE`: `envs/.env.deploy.beta` sets
 * `VITE_STAGE=prod`, so a stage check would leave beta enabled. `VITE_UI_URL`
 * is the only var that separates the two. Read at call time rather than at
 * module load so tests can stub it.
 *
 * Deliberately unlike the hostname checks in `MixpanelConfig.ts` and
 * `index.tsx`, which are equally correct about beta: an env var can be stubbed
 * per test, which is what lets the spec pin `VITE_UI_URL` and `VITE_STAGE`
 * together and actually guard the trap above. Do not fold the four into one
 * "is prod" helper without keeping that test.
 */
const isProductionAdsEnvironment = (): boolean =>
  import.meta.env.VITE_UI_URL === PROD_UI_URL;

/**
 * The conversion action to report against, or `null` when this build must not
 * report at all — which is every environment except production.
 *
 * Callers treat `null` as "do nothing, and do not load the tag either": the
 * point is that non-production never contacts Google Ads, not that it reports
 * somewhere harmless.
 */
export const resolveInstallConversionTarget = (
  config: GoogleAdsConfigType
): string | null =>
  isProductionAdsEnvironment()
    ? `${config.conversionId}/${config.prodInstallLabel}`
    : null;
